import { ReactNode } from "react";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/atoms/dialog";
import { Button } from "@/components/atoms/button";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useGlobalText } from "@/contexts/GlobalTextContext";

interface ModalProps {
  title?: string;
  description?: string;
  trigger?: ReactNode;
  actionText?: string;
  header?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  onAction?: () => void;
  onCancel?: () => void;
  open: boolean;
  toggleModal: () => void;
  containerClassName?: string;
  footerClassName?: string;
  closeClassName?: string;
  headerClassName?: string;
}

/**
 * Modal Component
 *
 * A customizable modal built using Radix UI Dialog primitives.
 * Supports dynamic header, footer, custom trigger, and action handling.
 * Cancel text comes from CMS via context, with optional override.
 */
export default function Modal({
  title,
  description,
  trigger,
  header,
  children,
  footer,
  actionText,
  onAction,
  open,
  toggleModal,
  onCancel,
  containerClassName = "",
  closeClassName = "",
  footerClassName = "",
  headerClassName = "",
  ...props
}: Readonly<ModalProps>) {
  // Get cancel text from CMS context, allow override via prop
  const { cancelText: modalCancelText, closeText: modalConfirmClosedText } =
    useGlobalText();
  return (
    // Root dialog component controlled via `open` and `toggleModal`
    <Dialog open={open} onOpenChange={toggleModal} {...props}>
      <DialogTrigger asChild>
        {typeof trigger === "string" ? <Button>{trigger}</Button> : trigger}
      </DialogTrigger>
      <DialogContent
        onPointerDownOutside={(event) => {
          event.preventDefault();
          toggleModal();
        }}
        className={cn("bg-[var(--fill-base-white)] p-0", containerClassName)}
        isDialogBaseComponent={true}
        onEscapeKeyDown={(event) => {
          event.preventDefault();
          toggleModal();
        }}
      >
        <DialogHeader className={cn(headerClassName)}>
          <DialogPrimitive.Close
            className={cn(
              `absolute top-4 right-4 z-20 cursor-pointer text-[var(--fill-gray-stronger)] opacity-70 transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*="size-"])]:size-6`,
              closeClassName,
            )}
          >
            <XIcon />
            <span className="sr-only">{modalConfirmClosedText}</span>
          </DialogPrimitive.Close>
          {header ?? (
            <>
              {title && (
                <DialogTitle className="border-b border-[var(--stroke-gray-light)] px-6 py-4 text-lg font-semibold text-[var(--text-gray-stronger)]">
                  {title}
                </DialogTitle>
              )}
              {description && (
                <DialogDescription className="px-6 py-2 text-sm text-[var(--text-gray-normal)]">
                  {description}
                </DialogDescription>
              )}
            </>
          )}
        </DialogHeader>
        {children}
        <DialogFooter className={cn(footerClassName)}>
          {footer ?? (
            <div className="flex justify-end space-x-2 px-6 py-4">
              <Button
                className="px-10 py-2"
                variant="neutral"
                hierarchy="ghost"
                onClick={onCancel ?? toggleModal}
              >
                {modalCancelText}
              </Button>
              <Button className="px-10 py-2" onClick={onAction}>
                {actionText}
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
